import { uploadName } from '@/apps/latest/features/attachments/recordingImageApi'
import client from '@/shared/apis/client'

// 報告書の欄ごとの文字列。キーは reportFields の REPORT_KEYS (= back の REPORT_KEYS) に限る。
export type NutritionReportFields = Record<string, string>

// 出力用の患者 ID。店舗ごとに別で 1 人に N 個 (必須でない・検索しない)。
export interface NutritionPatientId {
  store: string
  id: string
}

// お客様 (back の NutritionCustomer)。last_started_on = 最後に記録を始めた日 (左ペインの行の右端。中身の無い器は数えない)。
export interface NutritionCustomer {
  id: string
  name: string
  name_kana: string
  sex: string
  birth_date: string | null
  patient_ids: NutritionPatientId[]
  last_started_on: string | null
}

// お客様の登録・編集で送る欄 (last_started_on は読み出し専用で送らない)。
export type NutritionCustomerInput = Partial<
  Pick<
    NutritionCustomer,
    'name' | 'name_kana' | 'sex' | 'birth_date' | 'patient_ids'
  >
>

// 問診票から読んだ 4 欄の下書き (保存はしない)。読めなかった欄は空文字。
export type NutritionCustomerDraft = Pick<
  NutritionCustomer,
  'name' | 'name_kana' | 'sex'
> & { birth_date: string }

// 記録の段 (back の pipeline.stage が 1 か所で決める。画面は録音の engine の段と合わせるだけ)。
// 処理中のまま止まった記録は失敗の段で来る。regenerate_failed = 着地した報告書の作り直しが落ちた・止まった
export type NutritionStage =
  | 'before'
  | 'transcribing'
  | 'ready'
  | 'generating'
  | 'transcribe_failed'
  | 'generate_failed'
  | 'completed'
  | 'regenerate_failed'

export interface NutritionReport {
  stage: NutritionStage
  // 報告書が着地した時刻 (作る が成功した)。null = まだ作っていない。
  generated_at: string | null
  fields: NutritionReportFields
  // 記録のお客様。お客様の値 (年齢・性別・患者 ID) は GET customers/<id>/ が 1 つの正 (記録に写した欄は fields)
  customer_id: string
  // 出すときにコードが書く値。格子の同じ位置に見せる (欄ではないので直せない)。
  stamp: NutritionStamp
  // 生成が読んだ資料と読めずに飛ばした資料の id。null = 残していない (作る前・列を足す前に作った報告書)
  attachment_ids: string[] | null
  skipped_attachment_ids: string[] | null
  // 栄養価の節 (back の services/intake.py の view。報告書と一緒に着地する)。null = まだ計算していない
  // (計算できなかった・前の記録)
  intake: NutritionIntake | null
}

// 栄養価の 5 成分 (成分表の成分識別子。エネルギー kcal・たんぱく質・脂質・炭水化物・食塩相当量)
export const INTAKE_KEYS = [
  'ENERC_KCAL',
  'PROT-',
  'FAT-',
  'CHOCDF-',
  'NACL_EQ',
] as const
export type IntakeKey = (typeof INTAKE_KEYS)[number]
export type IntakeAmounts = Record<IntakeKey, number | null>

// 材料 1 つ。数字は 1 回分。code = 成分表の食品番号 (空 = 当てられなかった。合計に入らない)。
// path は back の保存の形の位置 (直す 1 手で送る)。
export interface NutritionIntakeFood {
  path: [number, number, number]
  name: string
  code: string
  label: string
  grams: number
  amounts: IntakeAmounts
}

export interface NutritionIntakeDish {
  path: [number, number]
  dish: string
  foods: NutritionIntakeFood[]
}

// 食事 (朝食・昼食・夕食・間食。飲料は間食)。小計は材料の和 (1 日分の食事)
export interface NutritionIntakeMeal {
  meal: string
  dishes: NutritionIntakeDish[]
  subtotal: IntakeAmounts
}

export interface NutritionIntake {
  meals: NutritionIntakeMeal[]
  total: IntakeAmounts
  // 当てられなかった材料の数 (合計に入っていない)
  unmatched: number
  // 計算の後に報告書の食事の欄が直された (「食事の欄から計算し直す」を出す。Excel は栄養価のシートを入れない)
  stale: boolean
  // 人が直した計算 (計算し直すと食品と g が置き換わるので、押したときに 1 行の確認を挟む)
  edited: boolean
  source: string
}

// 人の 1 手 (back の apply_edit)
export type NutritionIntakeOp =
  | { op: 'food'; path: [number, number, number]; code: string; grams: number }
  | { op: 'add'; path: [number, number]; code: string; grams: number }
  | { op: 'remove'; path: [number, number, number] }

export interface NutritionFood {
  code: string
  label: string
}

// カルテの回 (back の crud.rounds。中身の無い器は来ない)。id は録音イベントの id (回の口 records/<id>/)。
// recorded_on は実施日 (開始の日。回の選択の字)、landed は報告書が着地したか、current は今回か
// (いちばん新しい回が、まだ着地していないか今日始めたものなら、その回。決めるのは back で、画面は選ばない)。
export interface NutritionRound {
  id: string
  recorded_on: string
  session_number: string
  landed: boolean
  current: boolean
}

// 実施日 (開始の日 = 録音の開始の日。録音が届くまでは記録を作った日)・指導時間 (録音の長さ。音声が無ければ null)・署名 (アカウントの店と姓名)。
export interface NutritionStamp {
  recorded_on: string
  minutes: number | null
  store: string
  dietitian: string
}

// 出す宛先。back の services/excel.py の SHEETS のキー。
export type NutritionDestination = 'medical' | 'pharmacy' | 'cafe'

// 根は 1 つ。記録 (録音イベント 1 件 = 報告書 1 枚) の口は records/<event_id>/ の下に並ぶ。
const root = '/api/nutrition-counseling/'
const recordPath = (eventId: string) => `${root}records/${eventId}/`

// POST records/<id>/intake/: 食事の欄から栄養価を計算し直す (同期)。報告書の応答 (intake 入り) を返す。
export const recomputeNutritionIntake = async (
  eventId: string
): Promise<NutritionReport> => {
  const res = await client.post<NutritionReport>(
    `${recordPath(eventId)}intake/`
  )
  return res.data
}

// PATCH records/<id>/intake/: 栄養価の人の 1 手。報告書の応答を返す。
export const patchNutritionIntake = async (
  eventId: string,
  op: NutritionIntakeOp
): Promise<NutritionReport> => {
  const res = await client.patch<NutritionReport>(
    `${recordPath(eventId)}intake/`,
    op
  )
  return res.data
}

// GET foods/?q=: 成分表の食品を似た名前で 15 件
export const searchNutritionFoods = async (
  q: string
): Promise<NutritionFood[]> => {
  const res = await client.get<NutritionFood[]>(`${root}foods/`, {
    params: { q },
  })
  return res.data
}

// PATCH records/<id>/: 記録の担当 (署名の管理栄養士) を替える。どの段でも。null で外す。
export const patchNutritionRecordStaff = async (
  eventId: string,
  pharmacistId: string | null
): Promise<void> => {
  await client.patch(recordPath(eventId), { pharmacist_id: pharmacistId })
}

// DELETE records/<id>/: 204 = 記録を消した (報告書・添付資料・メモ・音声・文字起こしごと。元に戻せない)。
// 409 = 処理中 (文字起こし中・作成中)。
export const deleteNutritionRecord = async (eventId: string): Promise<void> => {
  await client.delete(recordPath(eventId))
}

// GET: 記録は報告書の行と一緒に生まれるので、録る前から在る。404 (他店舗・管理栄養士の記録でない) は呼び出し側に投げる。
export const getNutritionReport = async (
  eventId: string
): Promise<NutritionReport> => {
  const res = await client.get<NutritionReport>(`${recordPath(eventId)}report/`)
  return res.data
}

// PATCH: **触ったキーだけ** を送る。back は送られたキーだけを差し替えるので、往復中に
// 別の欄を保存しても先の変更が消えない (在宅の報告書と同じ契約)。応答は全欄。
export const patchNutritionReport = async (
  eventId: string,
  fields: NutritionReportFields
): Promise<NutritionReport> => {
  const res = await client.patch<NutritionReport>(
    `${recordPath(eventId)}report/`,
    { fields }
  )
  return res.data
}

// POST audio: 202 = 受けて文字起こしを開始 (event.status が processing になる)。
// audio を渡さなければ、置いてある音声で文字起こしをやり直す (文字起こしの失敗からの戻り道)。
// 409 = 処理中 / やり直す音声が無い。
export const postNutritionAudio = async (
  eventId: string,
  audio?: { blob: Blob; durationSeconds: number }
): Promise<void> => {
  const formData = new FormData()
  if (audio) {
    // MediaRecorder は audio/mp4 を優先し、未対応のブラウザでは WebM になる (共有 engine)
    const type = audio.blob.type || 'audio/mp4'
    const ext = type.includes('webm') ? 'webm' : 'm4a'
    formData.append(
      'audio',
      new File([audio.blob], `recording.${ext}`, { type })
    )
    formData.append('duration_seconds', String(audio.durationSeconds))
  }
  await client.post(`${recordPath(eventId)}audio/`, formData)
}

// DELETE audio: 204 = 録音 (音声と文字起こし) を捨てて記録を録音前に戻した。録り直すの 1 手目で、
// マイクはこの後に起動する (完了・失敗のままマイクを起動しない)。409 = 処理中 / 報告書が着地済み。
export const deleteNutritionAudio = async (eventId: string): Promise<void> => {
  await client.delete(`${recordPath(eventId)}audio/`)
}

// POST generate: 202 = 作り始めた (event.status が processing になる)。着地していれば作り直す
// (お客様の欄は残し、ほかは全置換)。409 = 文字起こしが済んでいない / 処理中。
export const generateNutritionReport = async (
  eventId: string
): Promise<void> => {
  await client.post(`${recordPath(eventId)}generate/`)
}

// GET xlsx: 宛先の様式 1 枚。ファイル名は呼び出し側が付ける (Content-Disposition は
// 別オリジンの開発環境では読めない)。
export const downloadNutritionReportXlsx = async (
  eventId: string,
  to: NutritionDestination
): Promise<Blob> => {
  const res = await client.get<Blob>(`${recordPath(eventId)}xlsx/`, {
    params: { to },
    responseType: 'blob',
  })
  return res.data
}

const customersPath = `${root}customers/`

// GET ?q= : 氏名・ふりがな・生年月日の部分一致。最後に記録を始めた日の新しい順 → 記録の無い人は登録の新しい順 (50 件まで)。
export const searchNutritionCustomers = async (
  q: string
): Promise<NutritionCustomer[]> => {
  const res = await client.get<NutritionCustomer[]>(customersPath, {
    params: { q },
  })
  return res.data
}

// GET similar/: 登録の前に出す似たお客様 (同じ店で、ふりがな か 生年月日 が同じ人)。どちらも空なら空。
export const similarNutritionCustomers = async (
  nameKana: string,
  birthDate: string | null
): Promise<NutritionCustomer[]> => {
  const res = await client.get<NutritionCustomer[]>(
    `${customersPath}similar/`,
    { params: { name_kana: nameKana, birth_date: birthDate ?? '' } }
  )
  return res.data
}

export const createNutritionCustomer = async (
  input: NutritionCustomerInput
): Promise<NutritionCustomer> => {
  const res = await client.post<NutritionCustomer>(customersPath, input)
  return res.data
}

export const getNutritionCustomer = async (
  customerId: string
): Promise<NutritionCustomer> => {
  const res = await client.get<NutritionCustomer>(
    `${customersPath}${customerId}/`
  )
  return res.data
}

// PATCH: 送った欄だけ直る。記録に写した欄には書き戻らない。
export const patchNutritionCustomer = async (
  customerId: string,
  input: NutritionCustomerInput
): Promise<NutritionCustomer> => {
  const res = await client.patch<NutritionCustomer>(
    `${customersPath}${customerId}/`,
    input
  )
  return res.data
}

// GET records: カルテの回を新しい順に (今回と着地の印つき)。
export const listNutritionRounds = async (
  customerId: string
): Promise<NutritionRound[]> => {
  const res = await client.get<NutritionRound[]>(
    `${customersPath}${customerId}/records/`
  )
  return res.data
}

// POST records: 今回を始める。今回があればその id、無ければ記録を作る (録音イベントは PENDING で待つ)。
// pharmacistId = 担当 (店の名簿。署名の管理栄養士)。その PC で前に選んだ人を渡す。
export const startNutritionRound = async (
  customerId: string,
  pharmacistId?: string
): Promise<{ event_id: string }> => {
  const res = await client.post<{ event_id: string }>(
    `${customersPath}${customerId}/records/`,
    pharmacistId ? { pharmacist_id: pharmacistId } : undefined
  )
  return res.data
}

// POST draft: 問診票の画像から 4 欄を下書き (同期・保存しない)。502 = 読み取れなかった。
export const draftNutritionCustomer = async (
  image: Blob
): Promise<NutritionCustomerDraft> => {
  const formData = new FormData()
  formData.append('image', image, uploadName(image, 'questionnaire'))
  const res = await client.post<NutritionCustomerDraft>(
    `${customersPath}draft/`,
    formData
  )
  return res.data
}
